import { Controller } from "@hotwired/stimulus"

// Interface translation — hand-authored, no external service.
//
// Strategy: translate by matching the EXACT English source string in the DOM
// (text nodes + tooltip/placeholder/aria attributes), so we don't have to tag
// thousands of elements across the ERB editor pane + JS wizard pane. Originals
// are stashed in a WeakMap so switching back to English restores them, and a
// MutationObserver re-applies translations as the wizard re-renders and lazy
// sections mount. User-entered content is never touched: we skip
// <textarea>/<input> values (the value attr is not translated) and <option>s.
//
// Coverage is phased. Labels that embed a [FirstName]/[LastName]/[Age] token
// are intentionally NOT in the dictionary yet — the token renders as its own
// <span>, so the surrounding text is a separate node the whole-string match
// can't catch. Those need placeholder-aware handling (a later refinement).
//
// DICT is keyed by the exact English string → { es, ko }. Add a row to grow it.
// Korean strings are a first pass — worth a native-speaker review.
//
// Mounted on .en-app; driven by the toolbar language picker's
// `pcp:language-change` event + plan.meta.language on load.

const STORAGE_KEY = "pcp_plan_data"
const ATTRS = ["placeholder", "title", "aria-label"]
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "TEXTAREA", "OPTION", "NOSCRIPT"])

const DICT = {
  // ---- Section nav + editor/wizard chrome ----
  "Section Tree": { es: "Árbol de secciones", ko: "섹션 목록" },
  "Wizard": { es: "Asistente", ko: "마법사" },
  "Open Questions": { es: "Preguntas abiertas", ko: "미해결 질문" },
  "Client": { es: "Cliente", ko: "고객" },
  "About": { es: "Acerca de", ko: "소개" },
  "Circle": { es: "Círculo", ko: "서클" },
  "Communication": { es: "Comunicación", ko: "의사소통" },
  "Strengths & more": { es: "Fortalezas y más", ko: "강점 및 기타" },
  "Not Favorites": { es: "No favoritos", ko: "비선호" },
  "Day in Life": { es: "Un día en la vida", ko: "하루 일과" },
  "Smart Calendar": { es: "Calendario inteligente", ko: "스마트 캘린더" },
  "Important": { es: "Importante", ko: "중요" },
  "Dreams": { es: "Sueños", ko: "꿈" },
  "Support": { es: "Apoyo", ko: "지원" },
  "Working": { es: "Lo que funciona", ko: "잘 되는 것" },
  "Barriers": { es: "Barreras", ko: "장벽" },
  "Goals": { es: "Metas", ko: "목표" },
  "Current Services": { es: "Servicios actuales", ko: "현재 서비스" },
  "Unmet Needs": { es: "Necesidades no cubiertas", ko: "미충족 요구" },
  "Review": { es: "Revisión", ko: "검토" },
  "Revision": { es: "Revisiones", ko: "수정 이력" },
  "Review & Sign Page": { es: "Página de revisión y firma", ko: "검토 및 서명 페이지" },
  "PCP language": { es: "Idioma del PCP", ko: "PCP 언어" },

  // ---- Shared list / control chrome ----
  "Add Item": { es: "Agregar elemento", ko: "항목 추가" },
  "Add Row": { es: "Agregar fila", ko: "행 추가" },
  "Add Person": { es: "Agregar persona", ko: "사람 추가" },
  "Bulk Entry": { es: "Entrada masiva", ko: "일괄 입력" },
  "Bulk Add": { es: "Entrada masiva", ko: "일괄 입력" },
  "Clear": { es: "Borrar", ko: "지우기" },
  "Clear All": { es: "Borrar todo", ko: "모두 지우기" },
  "Remove": { es: "Quitar", ko: "삭제" },
  "Close": { es: "Cerrar", ko: "닫기" },
  "Drag to reorder": { es: "Arrastra para reordenar", ko: "드래그하여 순서 변경" },
  "Nothing added yet.": { es: "Aún no se ha agregado nada.", ko: "아직 추가된 항목이 없습니다." },
  "No entries yet.": { es: "Aún no hay entradas.", ko: "아직 항목이 없습니다." },
  "No items yet.": { es: "Sin elementos aún.", ko: "아직 항목이 없습니다." },
  "No selected yet.": { es: "Aún no se ha seleccionado nada.", ko: "아직 선택된 항목이 없습니다." },
  "No one listed yet.": { es: "Aún no hay nadie en la lista.", ko: "아직 추가된 항목이 없습니다." },
  "selected": { es: "seleccionado(s)", ko: "선택됨" },
  "entries": { es: "entradas", ko: "항목" },
  "members": { es: "miembros", ko: "구성원" },
  "Show all": { es: "Mostrar todo", ko: "모두 보기" },
  "Show all options": { es: "Mostrar todas las opciones", ko: "모든 옵션 표시" },
  "Showing just the block you clicked in the editor": { es: "Mostrando solo el bloque que hiciste clic en el editor", ko: "편집기에서 클릭한 블록만 표시" },
  "Showing just the group you clicked in the editor": { es: "Mostrando solo el grupo que hiciste clic en el editor", ko: "편집기에서 클릭한 그룹만 표시" },
  "+ Add": { es: "+ Agregar", ko: "+ 추가" },
  "+ TBD": { es: "+ Pendiente", ko: "+ 미정" },
  "Press Enter to add · Shift+Enter for new line · type a number to add that chip · Tap + TBD to mark as unknown": { es: "Presiona Enter para agregar · Shift+Enter para nueva línea · escribe un número para agregar ese chip · Toca + Pendiente para marcar como desconocido", ko: "Enter를 눌러 추가 · Shift+Enter로 새 줄 · 번호를 입력하여 해당 칩 추가 · + 미정을 탭하여 미정으로 표시" },
  "Or type your own…": { es: "O escribe el tuyo…", ko: "또는 직접 입력하세요…" },
  "Click to fill in details": { es: "Haz clic para completar detalles", ko: "세부 정보를 입력하려면 클릭하세요" },
  "Click to edit": { es: "Haz clic para editar", ko: "편집하려면 클릭하세요" },
  "This section is content-only — edit it directly on the left.": { es: "Esta sección es solo de contenido — edítala directamente a la izquierda.", ko: "이 섹션은 콘텐츠 전용입니다 — 왼쪽에서 직접 편집하세요." },

  // ---- Client section ----
  "Client Information": { es: "Información del cliente", ko: "고객 정보" },
  "Basic details about the participant.": { es: "Datos básicos sobre el participante.", ko: "참가자에 대한 기본 정보." },
  "DDS Age Group": { es: "Grupo de edad DDS", ko: "DDS 연령 그룹" },
  "Calculated from Date of Birth": { es: "Calculado a partir de la fecha de nacimiento", ko: "생년월일로 계산됨" },
  "Adult (22+ years)": { es: "Adulto (22+ años)", ko: "성인 (22세 이상)" },
  "School Age (3-15 years)": { es: "Edad escolar (3-15 años)", ko: "학령기 (3-15세)" },
  "Participant Photo": { es: "Foto del participante", ko: "참가자 사진" },
  "Life Representation Photo": { es: "Foto representativa de la vida", ko: "삶을 나타내는 사진" },
  "A photo that represents your life.": { es: "Una foto que representa tu vida.", ko: "당신의 삶을 나타내는 사진." },
  "Life Photo Caption": { es: "Descripción de la foto de vida", ko: "삶 사진 설명" },
  "First Name": { es: "Nombre", ko: "이름" },
  "Last Name": { es: "Apellido", ko: "성" },
  "Date of Birth": { es: "Fecha de nacimiento", ko: "생년월일" },
  "Gender": { es: "Género", ko: "성별" },
  "First name": { es: "Nombre", ko: "이름" },
  "Last name": { es: "Apellido", ko: "성" },
  "Date of birth": { es: "Fecha de nacimiento", ko: "생년월일" },
  "What does the team call this person?": { es: "¿Cómo llama el equipo a esta persona?", ko: "팀은 이 사람을 뭐라고 부르나요?" },
  "Family or chosen surname.": { es: "Apellido familiar o elegido.", ko: "가족 또는 선택한 성." },
  "Used for the plan header. We use this to adjust later prompts (school vs. work).": { es: "Se usa en el encabezado del plan. Lo usamos para ajustar las preguntas posteriores (escuela vs. trabajo).", ko: "계획 머리글에 사용됩니다. 이후 질문(학교 대 직장)을 조정하는 데 사용합니다." },
  "Unknown — mark as TBD": { es: "Desconocido — marcar como pendiente", ko: "알 수 없음 — 미정으로 표시" },

  // ---- About + Communication ----
  "Communication, language, culture, living situation, school, and work.": { es: "Comunicación, idioma, cultura, situación de vida, escuela y trabajo.", ko: "의사소통, 언어, 문화, 거주 상황, 학교 및 직업." },
  "Describe how you communicate — verbal, AAC, gestures, etc.": { es: "Describe cómo te comunicas — verbal, CAA, gestos, etc.", ko: "의사소통 방식을 설명하세요 — 음성, AAC, 제스처 등." },
  "Primary language(s) at home": { es: "Idioma(s) principal(es) en casa", ko: "집에서 사용하는 주요 언어" },
  "e.g. English, Spanish": { es: "p. ej. Inglés, Español", ko: "예: 영어, 스페인어" },
  "Cultural preferences and details (if any)": { es: "Preferencias culturales y detalles (si las hay)", ko: "문화적 선호도 및 세부 사항(있는 경우)" },
  "e.g. Halal diet · Diwali · Traditional music": { es: "p. ej. Dieta halal · Diwali · Música tradicional", ko: "예: 할랄 음식 · 디왈리 · 전통 음악" },
  "Living situation": { es: "Situación de vida", ko: "거주 상황" },
  "e.g. Family home · Foster home · Group home · or specify…": { es: "p. ej. Casa familiar · Hogar de acogida · Casa grupal · u otro…", ko: "예: 가족 집 · 위탁 가정 · 집단 생활 · 또는 기타…" },
  "School name, type of program, grade level, etc.": { es: "Nombre de la escuela, tipo de programa, nivel de grado, etc.", ko: "학교 이름, 프로그램 유형, 학년 등." },
  "Employment, volunteer work, day program, or vocational training.": { es: "Empleo, trabajo voluntario, programa diario o capacitación vocacional.", ko: "고용, 자원봉사, 주간 프로그램 또는 직업 훈련." },
  "Select all that apply.": { es: "Selecciona todos los que correspondan.", ko: "해당하는 항목을 모두 선택하세요." },
  "School": { es: "Escuela", ko: "학교" },
  "e.g. 4th grade at Lincoln Elementary": { es: "p. ej. 4º grado en Lincoln Elementary", ko: "예: Lincoln Elementary의 4학년" },
  "Tap a quick option, then type the name or details.": { es: "Toca una opción rápida y luego escribe el nombre o detalles.", ko: "빠른 옵션을 탭한 후 이름이나 세부 사항을 입력하세요." },
  "Work or day program": { es: "Trabajo o programa diario", ko: "직업 또는 주간 프로그램" },
  "e.g. Volunteers at the library on Saturdays": { es: "p. ej. Voluntario en la biblioteca los sábados", ko: "예: 토요일 도서관에서 자원봉사" },
  "Tap a quick option to add — type details after.": { es: "Toca una opción rápida para agregar — escribe los detalles después.", ko: "빠른 옵션을 탭하여 추가 — 이후 세부 사항을 입력합니다." },
  "Tap any that apply, or type your own. Flags interpreter / translation needs.": { es: "Toca cualquiera que se aplique, o escribe el tuyo. Señala necesidades de intérprete / traducción.", ko: "해당하는 항목을 탭하거나 직접 입력하세요. 통역/번역 필요 사항을 표시합니다." },
  "Tap an option, or choose Other to type your own.": { es: "Toca una opción, o elige Otro para escribir la tuya.", ko: "옵션을 탭하거나 직접 입력할 '기타'를 선택하세요." },
  "Please specify the living situation…": { es: "Por favor, especifica la situación de vida…", ko: "거주 상황을 구체적으로 입력하세요…" },
  "Behavior Communication Chart": { es: "Gráfico de Comunicación Conductual", ko: "행동 의사소통 차트" },
  "Understanding behavior as communication.": { es: "Entendiendo el comportamiento como comunicación.", ko: "행동을 의사소통으로 이해하기." },
  "Communication Chart": { es: "Gráfico de Comunicación", ko: "의사소통 차트" },
  "Tap any cell · pick from the options · chain to the next column.": { es: "Toca cualquier celda · elige entre las opciones · encadena a la siguiente columna.", ko: "셀을 탭 · 옵션 선택 · 다음 열로 연결." },
  "What's happening": { es: "Qué está pasando", ko: "일어나고 있는 일" },
  "Behavior": { es: "Comportamiento", ko: "행동" },
  "What it means": { es: "Lo que significa", ko: "의미" },
  "Support / response": { es: "Apoyo / respuesta", ko: "지원 / 대응" },
  "Click a cell to open the picker on the right pane.": { es: "Haz clic en una celda para abrir el selector en el panel derecho.", ko: "오른쪽 패널에서 선택기를 열려면 셀을 클릭하세요." },
  "Communication picker": { es: "Selector de Comunicación", ko: "의사소통 선택기" },
  "Click any cell on the chart to begin": { es: "Haz clic en cualquier celda del gráfico para comenzar", ko: "차트의 셀을 클릭하여 시작하세요" },
  "Pick a situation → behaviors filter to fit it → meanings filter to that behavior → supports narrow to the meaning.": { es: "Elige una situación → los comportamientos se filtran para ajustarse → los significados se filtran para ese comportamiento → los apoyos se ajustan al significado.", ko: "상황 선택 → 행동 필터링 → 의미 필터링 → 지원 조정." },
  "Row 1 complete": { es: "Fila 1 completada", ko: "행 1 완료" },
  "Click another cell to keep building, or tap + Add Row on the chart for a fresh entry.": { es: "Haz clic en otra celda para continuar, o toca + Agregar Fila en el gráfico para una nueva entrada.", ko: "다른 셀을 클릭하여 계속하거나 차트에서 + 행 추가를 탭하여 새 항목을 추가하세요." },
  "Re-apply filter": { es: "Volver a aplicar filtro", ko: "필터 다시 적용" },

  // ---- Circle of Support ----
  "Circle of Support": { es: "Círculo de Apoyo", ko: "지원 서클" },
  "List the people who live in your home.": { es: "Lista las personas que viven en tu hogar.", ko: "당신의 집에 함께 사는 사람들을 나열하세요." },
  "Linked from the household list — edit there to update": { es: "Vinculado desde la lista de hogar — edita allí para actualizar", ko: "가구 목록에서 링크됨 — 거기서 편집하여 업데이트하세요" },
  "Name": { es: "Nombre", ko: "이름" },
  "Family": { es: "Familia", ko: "가족" },
  "Friend": { es: "Amigo", ko: "친구" },
  "Professional": { es: "Profesional", ko: "전문가" },
  "Community": { es: "Comunidad", ko: "커뮤니티" },
  "Service Dog": { es: "Perro de servicio", ko: "서비스견" },
  "TBD": { es: "Pendiente", ko: "미정" },
  "Add by category": { es: "Agregar por categoría", ko: "카테고리별 추가" },
  "Relationship": { es: "Relación", ko: "관계" },
  "Friend type": { es: "Tipo de amigo", ko: "친구 유형" },
  "Role / Title": { es: "Función / Puesto", ko: "직책 / 직무" },
  "Role / Connection": { es: "Función / Conexión", ko: "역할 / 연결" },
  "Family who lives outside the home": { es: "Familia que vive fuera del hogar", ko: "집 밖에 사는 가족" },
  "Under 18?": { es: "¿Menor de 18?", ko: "18세 미만?" },
  "Age": { es: "Edad", ko: "나이" },
  "+ Show more roles": { es: "+ Mostrar más funciones", ko: "+ 더 많은 역할 보기" },
  "− Hide extra roles": { es: "− Ocultar funciones adicionales", ko: "− 추가 역할 숨기기" },
  "Skip — mark as TBD": { es: "Omitir — marcar como pendiente", ko: "건너뛰기 — 미정으로 표시" },
  "Skip — fill missing fields with TBD; tracked in Open Questions": { es: "Omitir — rellenar campos faltantes con pendiente; rastreado en Preguntas abiertas", ko: "건너뛰기 — 누락된 필드를 미정으로 채우기(미해결 질문에서 추적됨)" },
  "Tap a chip to add — then type the name (e.g. tap \"Brother\", type \" Alex\"). Click any existing person on the left to jump to it here.": { es: "Toca un chip para agregar — luego escribe el nombre (p. ej., toca \"Hermano\", escribe \" Alex\"). Haz clic en cualquier persona existente a la izquierda para saltar aquí.", ko: "칩을 탭하여 추가한 후 이름을 입력하세요(예: \"형\"을 탭하고 \" Alex\"를 입력). 왼쪽의 기존 사람을 클릭하여 여기로 이동합니다." },

  // ---- Open Questions ----
  "Follow-ups to chase before the plan is final — never printed.": { es: "Seguimientos para resolver antes de que el plan sea final — nunca se imprime.", ko: "계획이 최종 확정되기 전에 확인할 후속 조치 — 인쇄되지 않습니다." },
  "Rescan": { es: "Escanear de nuevo", ko: "다시 검사" },
  "Email OpenQ": { es: "Enviar Preguntas abiertas", ko: "미해결 질문 이메일 보내기" },
  "Edit template": { es: "Editar plantilla", ko: "템플릿 편집" },
  "Email template": { es: "Plantilla de correo", ko: "이메일 템플릿" },
  "Subject": { es: "Asunto", ko: "제목" },
  "Body": { es: "Cuerpo", ko: "본문" },
  "Save template": { es: "Guardar plantilla", ko: "템플릿 저장" },
  "Reset to default": { es: "Restablecer a predeterminado", ko: "기본값으로 재설정" },

  // ---- Strengths & more / Not favorites ----
  "Capture the things that bring joy, happiness, and meaning.": { es: "Captura las cosas que traen alegría, felicidad y significado.", ko: "기쁨, 행복, 의미를 주는 것들을 포착합니다." },
  "Favorite Activities": { es: "Actividades favoritas", ko: "좋아하는 활동" },
  "What do you love doing?": { es: "¿Qué te encanta hacer?", ko: "무엇을 하는 것을 좋아하나요?" },
  "Type your own — Painting, swimming, music…": { es: "Escribe el tuyo — Pintura, natación, música…", ko: "직접 입력하기 — 그림 그리기, 수영, 음악…" },
  "Favorite Places": { es: "Lugares favoritos", ko: "좋아하는 장소" },
  "Where do you love to go?": { es: "¿Dónde te encanta ir?", ko: "어디를 가는 것을 좋아하나요?" },
  "Where they love to be.": { es: "Dónde les encanta estar.", ko: "그들이 가고 싶어하는 곳." },
  "e.g. The park, grandma's house": { es: "p. ej. El parque, casa de la abuela", ko: "예: 공원, 할머니 집" },
  "Special Events": { es: "Eventos especiales", ko: "특별한 행사" },
  "What events do you look forward to?": { es: "¿Qué eventos esperas?", ko: "어떤 행사를 기대하나요?" },
  "Events they look forward to.": { es: "Eventos que esperan.", ko: "그들이 기대하는 행사." },
  "e.g. Birthday parties, holiday gatherings": { es: "p. ej. Fiestas de cumpleaños, reuniones festivas", ko: "예: 생일 파티, 휴일 모임" },
  "Favorite Foods": { es: "Comidas favoritas", ko: "좋아하는 음식" },
  "What do you love to eat?": { es: "¿Qué te encanta comer?", ko: "무엇을 먹는 것을 좋아하나요?" },
  "Snacks, meals, treats.": { es: "Aperitivos, comidas, golosinas.", ko: "간식, 식사, 별미." },
  "e.g. Pizza, strawberries": { es: "p. ej. Pizza, fresas", ko: "예: 피자, 딸기" },
  "Strengths": { es: "Fortalezas", ko: "강점" },
  "What are your strengths and talents?": { es: "¿Cuáles son tus fortalezas y talentos?", ko: "당신의 강점과 재능은 무엇인가요?" },
  "What they're good at — personal qualities, talents, skills.": { es: "Lo que se les da bien — cualidades personales, talentos, habilidades.", ko: "그들이 잘하는 것 — 개인적인 특성, 재능, 기술." },
  "e.g. Kind, hard-working, great memory": { es: "p. ej. Amable, trabajador, excelente memoria", ko: "예: 친절, 근면, 훌륭한 기억력" },
  "Qualities Admired by Others": { es: "Cualidades admiradas por otros", ko: "다른 사람들이 존경하는 특성" },
  "What do others admire about you?": { es: "¿Qué admiran otros de ti?", ko: "다른 사람들이 당신의 어떤 점을 존경하나요?" },
  "What's unique and special — the things people who know them love most.": { es: "Lo que es único y especial — lo que más aman las personas que los conocen.", ko: "독특하고 특별한 점 — 그들을 아는 사람들이 가장 사랑하는 것." },
  "e.g. Bright smile, deep thinker": { es: "p. ej. Sonrisa radiante, pensador profundo", ko: "예: 밝은 미소, 깊이 있는 생각" },
  "Things That Can Be Challenging": { es: "Cosas que pueden ser desafiantes", ko: "도전이 될 수 있는 것들" },
  "Things that bother, upset, or just aren't your cup of tea.": { es: "Cosas que molestan, enojan, o simplemente no son lo tuyo.", ko: "귀찮거나, 화나게 하거나, 단순히 당신이 좋아하지 않는 것들." },
  "What tends to cause frustration or meltdowns?": { es: "¿Qué tiende a causar frustración o crisis?", ko: "무엇이 좌절감이나 폭발을 일으키나요?" },
  "Knowing what upsets helps the team prevent it.": { es: "Saber qué le molesta ayuda al equipo a prevenirlo.", ko: "무엇이 화나게 하는지 알면 팀이 이를 예방할 수 있습니다." },
  "e.g. Loud sudden noises": { es: "p. ej. Ruidos fuertes y repentinos", ko: "예: 갑자기 큰 소음" },
  "Sensory Sensitivities": { es: "Sensibilidades sensoriales", ko: "감각 민감성" },
  "Sounds, textures, lights, smells that bother you.": { es: "Sonidos, texturas, luces, olores que te molestan.", ko: "당신을 귀찮게 하는 소리, 질감, 빛, 냄새." },
  "Lights, sounds, textures, smells.": { es: "Luces, sonidos, texturas, olores.", ko: "빛, 소리, 질감, 냄새." },
  "e.g. Bright fluorescent lights": { es: "p. ej. Luces fluorescentes brillantes", ko: "예: 밝은 형광등" },
  "Social or daily scenarios that don't work well.": { es: "Escenarios sociales o cotidianos que no funcionan bien.", ko: "잘 작동하지 않는 사회적 또는 일상적인 상황." },
  "Things they'd rather not do.": { es: "Cosas que prefieren no hacer.", ko: "그들이 하고 싶지 않은 일." },
  "e.g. Crowded places": { es: "p. ej. Lugares abarrotados", ko: "예: 혼잡한 장소" },
  "Foods you avoid or can't stand.": { es: "Alimentos que evitas o no aguantas.", ko: "당신이 피하거나 견딜 수 없는 음식." },
  "e.g. Spicy foods": { es: "p. ej. Comidas picantes", ko: "예: 매운 음식" },
  "Places That Don't Work": { es: "Lugares que no funcionan", ko: "잘 안 맞는 장소" },
  "Settings or environments that are uncomfortable.": { es: "Entornos o ambientes que son incómodos.", ko: "불편한 환경 또는 장소." },
  "e.g. Noisy restaurants": { es: "p. ej. Restaurantes ruidosos", ko: "예: 시끄러운 레스토랑" },

  // ---- Day in Life ----
  "A Day in the Life": { es: "Un día en la vida", ko: "하루의 일과" },
  "Describing a typical day and weekend.": { es: "Describiendo un día típico y fin de semana.", ko: "전형적인 하루와 주말을 설명합니다." },
  "Weekdays (Monday – Friday)": { es: "Entre semana (lunes – viernes)", ko: "평일 (월요일 – 금요일)" },
  "Does not go to school": { es: "No va a la escuela", ko: "학교에 가지 않음" },
  "Morning Time": { es: "Tiempo matutino", ko: "아침 시간" },
  "School Time": { es: "Tiempo escolar", ko: "학교 시간" },
  "After School": { es: "Después de la escuela", ko: "방과 후" },
  "Before Bed": { es: "Antes de dormir", ko: "잠들기 전" },
  "After Bed": { es: "Después de dormir", ko: "잠든 후" },
  "Weekends (Saturday – Sunday)": { es: "Fin de semana (sábado – domingo)", ko: "주말 (토요일 – 일요일)" },
  "Copy All from Weekday": { es: "Copiar todo de entre semana", ko: "평일에서 모두 복사" },
  "Home & Community": { es: "Casa y comunidad", ko: "집과 지역사회" },
  "Same as weekday": { es: "Igual que entre semana", ko: "평일과 동일" },
  "Typical weekday + weekend routine.": { es: "Rutina típica de entre semana + fin de semana.", ko: "전형적인 평일 및 주말 루틴." },
  "🗓 Weekdays": { es: "🗓 Entre semana", ko: "🗓 평일" },
  "🎉 Weekends": { es: "🎉 Fin de semana", ko: "🎉 주말" },
  "Same every weekday": { es: "Igual cada día entre semana", ko: "매 평일 동일" },
  "Different per day": { es: "Diferente cada día", ko: "일별 다름" },
  "⏱ School Start": { es: "⏱ Inicio escolar", ko: "⏱ 학교 시작" },
  "⏱ School End": { es: "⏱ Fin escolar", ko: "⏱ 학교 종료" },
  "School Start": { es: "Inicio escolar", ko: "학교 시작" },
  "School End": { es: "Fin escolar", ko: "학교 종료" },
  "Click to unlink — edit independently": { es: "Haz clic para desvincular — edita de forma independiente", ko: "클릭하여 연결 해제 — 독립적으로 편집" },
  "Mirror the weekday equivalent": { es: "Refleja el equivalente de entre semana", ko: "평일에 상응하는 항목 미러링" },

  // ---- Important TO / FOR ----
  "Important TO vs. Important For": { es: "Importante PARA vs. Importante POR", ko: "중요한 것(TO) vs. 중요한 이유(FOR)" },
  "Balancing happiness with health & safety.": { es: "Equilibrando la felicidad con la salud y la seguridad.", ko: "행복과 건강 및 안전의 균형을 맞춥니다." },
  "What makes you happy, fulfilled, and content.": { es: "Lo que te hace feliz, pleno y contento.", ko: "행복, 성취감, 만족을 주는 것." },
  "Health, safety, and being a valued member of the community.": { es: "Salud, seguridad y ser un miembro valorado de la comunidad.", ko: "건강, 안전, 지역사회의 소중한 구성원이 되기." },
  "Add what matters most…": { es: "Añade lo que más importa…", ko: "가장 중요한 것을 추가하세요…" },
  "Things that bring happiness and fulfillment.": { es: "Cosas que traen felicidad y satisfacción.", ko: "행복과 성취감을 주는 것들." },
  "Add a need…": { es: "Añade una necesidad…", ko: "필요한 것을 추가하세요…" },
  "Things needed for health, safety, and wellbeing.": { es: "Cosas necesarias para la salud, la seguridad y el bienestar.", ko: "건강, 안전, 웰빙을 위해 필요한 것들." },

  // ---- Big Dreams ----
  "What do you want for your future?": { es: "¿Qué quieres para tu futuro?", ko: "당신의 미래를 위해 무엇을 원하시나요?" },
  "Hopes for the Next 12 Months": { es: "Esperanzas para los próximos 12 meses", ko: "앞으로 12개월의 희망" },
  "Short term goals or wishes.": { es: "Objetivos o deseos a corto plazo.", ko: "단기 목표 또는 소원." },
  "Big Dreams for the Future": { es: "Grandes sueños para el futuro", ko: "미래의 큰 꿈" },
  "Long term aspirations (e.g. travel, career).": { es: "Aspiraciones a largo plazo (por ejemplo, viajes, carrera).", ko: "장기 열망 (예: 여행, 직업)." },
  "🌱 Hopes (next 6–12 months)": { es: "🌱 Esperanzas (próximos 6–12 meses)", ko: "🌱 희망 (다음 6–12개월)" },
  "Add a hope…": { es: "Añade una esperanza…", ko: "희망을 추가하세요…" },
  "Short-term — things to accomplish or experience this year.": { es: "Corto plazo — cosas a lograr o experimentar este año.", ko: "단기 — 올해 이루거나 경험할 것들." },
  "🌟 Big Dreams (long-term)": { es: "🌟 Grandes sueños (largo plazo)", ko: "🌟 큰 꿈 (장기)" },
  "Add a dream…": { es: "Añade un sueño…", ko: "꿈을 추가하세요…" },
  "Long-term aspirations and life goals.": { es: "Aspiraciones a largo plazo y objetivos de vida.", ko: "장기 열망과 인생 목표." },

  // ---- Support Match / Working ----
  "Matching Support Tool": { es: "Herramienta de coincidencia de apoyo", ko: "지원 인력 매칭 도구" },
  "Characteristics of ideal support staff.": { es: "Características del personal de apoyo ideal.", ko: "이상적인 지원 인력의 특성." },
  "Qualities & Characteristics": { es: "Cualidades y características", ko: "자질 및 특성" },
  "e.g. Patience, energetic": { es: "p. ej. Paciencia, enérgico", ko: "예: 인내심, 활기찬" },
  "Skills Needed": { es: "Habilidades necesarias", ko: "필요한 기술" },
  "e.g. Driving, cooking": { es: "p. ej. Conducir, cocinar", ko: "예: 운전, 요리" },
  "Personality Match": { es: "Compatibilidad de personalidad", ko: "성격 맞춤" },
  "e.g. Cheerful, calm": { es: "p. ej. Alegre, tranquilo", ko: "예: 쾌활한, 침착한" },
  "Deal Breakers": { es: "Factores decisivos", ko: "결정적 요인" },
  "What doesn't work": { es: "Lo que no funciona", ko: "작동하지 않는 것" },
  "What kind of person works well with me.": { es: "Qué tipo de persona trabaja bien conmigo.", ko: "나와 잘 맞는 사람의 유형." },
  "Specific skills the support person should have.": { es: "Habilidades específicas que debe tener la persona de apoyo.", ko: "지원 인력이 가져야 할 구체적인 기술." },
  "Personality style that fits well.": { es: "Estilo de personalidad que se adapta bien.", ko: "잘 맞는 성격 유형." },
  "What doesn't work — flag early so the team can avoid it.": { es: "Lo que no funciona — márcalo pronto para que el equipo pueda evitarlo.", ko: "작동하지 않는 것 — 팀이 피할 수 있도록 조기에 표시." },
  "What Works & Doesn't": { es: "Lo que funciona y lo que no", ko: "작동하는 것과 작동하지 않는 것" },
  "Capture both: what's helping today, and what isn't.": { es: "Captura ambos: lo que ayuda hoy y lo que no.", ko: "둘 다 기록: 오늘 도움이 되는 것과 그렇지 않은 것." },
  "What's Working": { es: "Lo que funciona", ko: "작동하는 것" },
  "Add a service that's helping…": { es: "Agregar un servicio que está ayudando…", ko: "도움이 되는 서비스 추가…" },
  "Services or supports that are helping.": { es: "Servicios o apoyos que están ayudando.", ko: "도움이 되는 서비스 또는 지원." },
  "What's Not Working": { es: "Lo que no funciona", ko: "작동하지 않는 것" },
  "Add a service that's not working…": { es: "Agregar un servicio que no está funcionando…", ko: "작동하지 않는 서비스 추가…" },
  "Services or supports that aren't meeting needs.": { es: "Servicios o apoyos que no están satisfaciendo las necesidades.", ko: "필요를 충족하지 못하는 서비스 또는 지원." },

  // ---- Goals / Barriers / Current Services / Unmet Needs ----
  "SMART Goals": { es: "Metas SMART", ko: "SMART 목표" },
  "Regional Center": { es: "Centro Regional", ko: "지역 센터" },
  "Heading (optional)": { es: "Encabezado (opcional)", ko: "제목 (선택)" },
  "Description": { es: "Descripción", ko: "설명" },
  "Timeline": { es: "Cronograma", ko: "일정" },
  "Resources": { es: "Recursos", ko: "리소스" },
  "Add Resource": { es: "Agregar recurso", ko: "리소스 추가" },
  "Reference Documents (optional)": { es: "Documentos de referencia (opcional)", ko: "참고 문서 (선택)" },
  "Add reference": { es: "Agregar referencia", ko: "참고 추가" },
  "Add New Goal": { es: "Agregar nueva meta", ko: "새 목표 추가" },
  "Current Barriers to Support Hopes and Dreams": { es: "Barreras actuales para apoyar esperanzas y sueños", ko: "희망과 꿈을 지원하는 현재 장벽" },
  "What obstacles stand in the way of achieving goals?": { es: "¿Qué obstáculos impiden lograr las metas?", ko: "목표 달성을 방해하는 장애물은 무엇인가요?" },
  "Barrier Description": { es: "Descripción de la barrera", ko: "장벽 설명" },
  "What is the barrier?": { es: "¿Cuál es la barrera?", ko: "장벽이 무엇입니까?" },
  "Strategy to Address": { es: "Estrategia para abordar", ko: "대처 전략" },
  "Proposed approach to overcome this barrier...": { es: "Enfoque propuesto para superar esta barrera...", ko: "이 장벽을 극복하기 위한 제안된 접근 방법..." },
  "Add New Barrier": { es: "Agregar nueva barrera", ko: "새 장벽 추가" },
  "Services and supports currently in place.": { es: "Servicios y apoyos actualmente vigentes.", ko: "현재 제공 중인 서비스 및 지원." },
  "Service descriptions in Review/PDF: OFF": { es: "Descripciones de servicios en Revisión/PDF: DESACTIVADO", ko: "검토/PDF의 서비스 설명: 끔" },
  "Recommended from Goals & Barriers": { es: "Recomendado de Metas y Barreras", ko: "목표 및 장벽에서 권장" },
  "No suggestions yet — add resources to your Goals or Barriers and they'll surface here.": { es: "Sin sugerencias aún — agrega recursos a tus Metas o Barreras y aparecerán aquí.", ko: "아직 제안 없음 — 목표 또는 장벽에 리소스를 추가하면 여기에 표시됩니다." },
  "Service List": { es: "Lista de servicios", ko: "서비스 목록" },
  "Each row tags the service as RC Funded or Generic. Use Bulk Entry to paste several at once.": { es: "Cada fila etiqueta el servicio como Financiado por RC o Genérico. Usa Entrada masiva para pegar varios a la vez.", ko: "각 행은 서비스를 RC 자금 또는 일반으로 태그합니다. 일괄 입력을 사용하여 여러 항목을 한 번에 붙여넣으세요." },
  "No current services listed yet.": { es: "Aún no hay servicios actuales enumerados.", ko: "아직 현재 서비스가 나열되지 않았습니다." },
  "Areas where additional support is required.": { es: "Áreas donde se requiere apoyo adicional.", ko: "추가 지원이 필요한 영역." },
  "Unmet Needs List": { es: "Lista de necesidades no cubiertas", ko: "미충족 요구 목록" },
  "Automatically lists every resource you tag \"Unmet Need\" in Goals or Barriers, labeled by its selected service.": { es: "Enumera automáticamente cada recurso que etiquetas como \"Necesidad no cubierta\" en Metas o Barreras, con la etiqueta de su servicio seleccionado.", ko: "목표 또는 장벽에서 \"미충족 요구\"로 태그한 모든 리소스를 자동으로 나열하며, 선택한 서비스로 표시합니다." },
  "No unmet needs yet — tag a resource \"Unmet Need\" in Goals or Barriers and it appears here.": { es: "Sin necesidades no cubiertas aún — etiqueta un recurso como \"Necesidad no cubierta\" en Metas o Barreras y aparecerá aquí.", ko: "아직 미충족 요구 없음 — 목표 또는 장벽의 리소스를 \"미충족 요구\"로 태그하면 여기에 나타납니다." },
  "Unmet needs are pulled automatically from resources tagged \"Unmet Need\" in Goals & Barriers.": { es: "Las necesidades no cubiertas se extraen automáticamente de los recursos etiquetados como \"Necesidad no cubierta\" en Metas y Barreras.", ko: "미충족 요구는 목표 및 장벽에서 \"미충족 요구\"로 태그한 리소스에서 자동으로 가져옵니다." },
  "🚧 Current Barriers": { es: "🚧 Barreras actuales", ko: "🚧 현재 장벽" },
  "Add a barrier…": { es: "Agregar una barrera…", ko: "장벽 추가…" },
  "Things that make it harder to reach goals right now.": { es: "Cosas que dificultan alcanzar las metas en este momento.", ko: "지금 목표를 달성하기 어렵게 만드는 것들." },
  "✨ Suggested goals from your data": { es: "✨ Metas sugeridas según tus datos", ko: "✨ 데이터로부터 제안된 목표" },
  "Built from the strengths, day-in-life entries, barriers, and services you added in earlier steps. Click to add — refine wording, domain, and timeline after.": { es: "Construido a partir de las fortalezas, entradas del día a día, barreras y servicios que agregaste en pasos anteriores. Haz clic para agregar — refina la redacción, el dominio y el cronograma después.", ko: "이전 단계에서 추가한 강점, 일상 항목, 장벽 및 서비스로부터 구축됩니다. 클릭하여 추가 — 나중에 문구, 영역 및 일정을 개선합니다." },
  "Add a few favorite activities, day-in-life details, or barriers in the earlier steps and we'll suggest easy goals here.": { es: "Agrega algunas actividades favoritas, detalles del día a día o barreras en los pasos anteriores y sugeriremos metas fáciles aquí.", ko: "이전 단계에서 즐겨하는 활동, 일상 세부 사항 또는 장벽을 추가하면 여기서 쉬운 목표를 제안합니다." },
  "🛠 Current Services": { es: "🛠 Servicios actuales", ko: "🛠 현재 서비스" },
  "Add a current service…": { es: "Agregar un servicio actual…", ko: "현재 서비스 추가…" },
  "Services currently being received.": { es: "Servicios que se reciben actualmente.", ko: "현재 받고 있는 서비스." }
}

export default class extends Controller {
  connect() {
    this._orig = new WeakMap()          // node → original English (string for text, {attr:en} for elements)
    this._lang = this._planLang()
    this._onLang = (e) => this.setLanguage((e.detail && e.detail.code) || "en")
    document.addEventListener("pcp:language-change", this._onLang)

    if (this._lang !== "en") this._translateTree(this.element)

    // Re-apply as panes re-render (wizard re-render, lazy sections, dynamic
    // lists). We only mutate text/attributes — never childList — so this never
    // observes its own writes (no loop).
    this._obs = new MutationObserver((muts) => {
      if (this._lang === "en") return
      for (const m of muts) {
        m.addedNodes.forEach(n => {
          if (n.nodeType === 1) this._translateTree(n)
          else if (n.nodeType === 3) this._translateText(n)
        })
      }
    })
    this._obs.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    document.removeEventListener("pcp:language-change", this._onLang)
    if (this._obs) this._obs.disconnect()
  }

  setLanguage(lang) {
    if (lang === this._lang) return
    this._lang = lang
    this._translateTree(this.element)   // restore→translate handled per node
  }

  _translateTree(root) {
    if (root.nodeType === 3) { this._translateText(root); return }
    if (root.nodeType !== 1) return
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    const texts = []
    while (walker.nextNode()) texts.push(walker.currentNode)
    texts.forEach(t => this._translateText(t))
    this._translateAttrs(root)
    root.querySelectorAll("*").forEach(el => this._translateAttrs(el))
  }

  _translateText(node) {
    const parent = node.parentNode
    if (!parent || SKIP_TAGS.has(parent.nodeName)) return
    const raw = node.nodeValue
    const trimmed = raw.trim()
    if (!trimmed) return
    let en = this._orig.get(node)
    if (en == null || typeof en !== "string") {
      if (!this._isKey(trimmed)) return       // only adopt real English source
      en = trimmed
      this._orig.set(node, en)
    }
    const target = this._render(en)
    const next = raw.match(/^\s*/)[0] + target + raw.match(/\s*$/)[0]
    if (node.nodeValue !== next) node.nodeValue = next
  }

  _translateAttrs(el) {
    if (el.nodeType !== 1) return
    for (const attr of ATTRS) {
      if (!el.hasAttribute(attr)) continue
      const cur = (el.getAttribute(attr) || "").trim()
      if (!cur) continue
      let store = this._orig.get(el)
      if (!store || typeof store !== "object") store = {}
      let en = store[attr]
      if (en == null) {
        if (!this._isKey(cur)) continue
        en = cur
        store[attr] = en
        this._orig.set(el, store)
      }
      const target = this._render(en)
      if (el.getAttribute(attr) !== target) el.setAttribute(attr, target)
    }
  }

  // English original → the string to display in the active language.
  _render(en) {
    if (this._lang === "en") return en
    const e = DICT[en]
    return (e && e[this._lang]) || en
  }

  _isKey(s) {
    return Object.prototype.hasOwnProperty.call(DICT, s)
  }

  _planLang() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      const p = raw ? JSON.parse(raw) : {}
      return (p.meta && p.meta.language) || "en"
    } catch (_) { return "en" }
  }
}
